import React, { useMemo } from 'react';
import { Project, Issue, User } from '../../types';
import {
  BarChart3,
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  TrendingUp,
  Calendar,
  Target
} from 'lucide-react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface ProjectDashboardProps {
  project: Project;
  issues: Issue[];
  users: User[];
  currentUser: User | null;
  onIssueClick: (issue: Issue) => void;
  onCreateIssue: () => void;
}

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  project,
  issues,
  users,
  currentUser,
  onIssueClick,
  onCreateIssue
}) => {
  // Calculate project statistics
  const stats = useMemo(() => {
    const totalIssues = issues.length;
    const completedIssues = issues.filter(issue => issue.status === 'Done').length;
    const inProgressIssues = issues.filter(issue => issue.status === 'In Progress').length;
    const todoIssues = issues.filter(issue => issue.status === 'To Do').length;
    const overdueIssues = issues.filter(issue =>
      issue.dueDate && new Date(issue.dueDate) < new Date() && issue.status !== 'Done'
    ).length;

    const completionRate = totalIssues > 0 ? (completedIssues / totalIssues) * 100 : 0;

    // Issues by priority
    const priorityStats = issues.reduce((acc, issue) => {
      acc[issue.priority] = (acc[issue.priority] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Issues by type
    const typeStats = issues.reduce((acc, issue) => {
      acc[issue.type] = (acc[issue.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Recent activity (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentIssues = issues.filter(issue =>
      issue.createdAt && new Date(issue.createdAt) > thirtyDaysAgo
    );

    return {
      totalIssues,
      completedIssues,
      inProgressIssues,
      todoIssues,
      overdueIssues,
      completionRate,
      priorityStats,
      typeStats,
      recentIssues: recentIssues.length
    };
  }, [issues]);

  // Chart data for issue status distribution
  const statusChartOptions: ApexOptions = {
    chart: {
      type: 'donut',
      height: 300,
      background: 'transparent',
    },
    labels: ['To Do', 'In Progress', 'Done'],
    colors: ['#ef4444', '#f59e0b', '#10b981'],
    legend: {
      position: 'bottom',
      labels: {
        colors: '#64748b'
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toFixed(1) + '%';
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Issues',
              color: '#64748b'
            }
          }
        }
      }
    }
  };

  const statusChartSeries = [stats.todoIssues, stats.inProgressIssues, stats.completedIssues];

  // Chart data for priority distribution
  const priorityChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 300,
      background: 'transparent',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 4
      }
    },
    colors: ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'],
    dataLabels: {
      enabled: false
    },
    xaxis: {
      categories: ['Highest', 'High', 'Medium', 'Low'],
      labels: {
        style: {
          colors: '#64748b'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#64748b'
        }
      }
    },
    grid: {
      show: false
    }
  };

  const priorityChartSeries = [{
    name: 'Issues',
    data: [
      stats.priorityStats['Highest'] || 0,
      stats.priorityStats['High'] || 0,
      stats.priorityStats['Medium'] || 0,
      stats.priorityStats['Lowest'] || 0
    ]
  }];

  // Recent issues for the table
  const recentIssues = useMemo(() => {
    return issues
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 5);
  }, [issues]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Project Overview Header */}
        <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-3xl shadow-lg">
                {project.avatar}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
                <p className="text-gray-600">Project Dashboard • {project.key}</p>
              </div>
            </div>
            <button
              onClick={onCreateIssue}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              + Create Issue
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Issues</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalIssues}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <Target className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center text-sm">
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-green-600 font-medium">{stats.completionRate.toFixed(1)}% Complete</span>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-green-600">{stats.completedIssues}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center text-sm">
                <span className="text-gray-500">of {stats.totalIssues} total</span>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-3xl font-bold text-blue-600">{stats.inProgressIssues}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center text-sm">
                <span className="text-gray-500">active tasks</span>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overdue</p>
                <p className="text-3xl font-bold text-red-600">{stats.overdueIssues}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center text-sm">
                <span className="text-red-500 font-medium">needs attention</span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Issue Status Distribution */}
          <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20">
            <div className="flex items-center space-x-2 mb-6">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Issue Status Distribution</h3>
            </div>
            {stats.totalIssues > 0 ? (
              <ReactApexChart
                options={statusChartOptions}
                series={statusChartSeries}
                type="donut"
                height={300}
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No issues yet</p>
                </div>
              </div>
            )}
          </div>

          {/* Priority Distribution */}
          <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20">
            <div className="flex items-center space-x-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <h3 className="text-lg font-semibold text-gray-900">Priority Distribution</h3>
            </div>
            {stats.totalIssues > 0 ? (
              <ReactApexChart
                options={priorityChartOptions}
                series={priorityChartSeries}
                type="bar"
                height={300}
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No issues yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Issues Table */}
        <div className="bg-white/70 backdrop-blur-lg rounded-xl p-6 shadow-lg border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Recent Issues</h3>
            </div>
            <button
              onClick={() => {/* Navigate to issues tab */}}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              View all →
            </button>
          </div>

          {recentIssues.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Issue</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Priority</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Assignee</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {recentIssues.map((issue) => {
                    const assignee = users.find(u => u.id === issue.assigneeId);
                    return (
                      <tr
                        key={issue.id}
                        onClick={() => onIssueClick(issue)}
                        className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-900">
                              {issue.key}
                            </span>
                            <span className="text-sm text-gray-600 truncate max-w-xs">
                              {issue.summary}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            issue.status === 'Done' ? 'bg-green-100 text-green-800' :
                            issue.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {issue.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            issue.priority === 'Highest' ? 'bg-red-100 text-red-800' :
                            issue.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                            issue.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {issue.priority}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {assignee ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-xs font-medium text-white">
                                {assignee.displayName.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm text-gray-600">{assignee.displayName}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Unassigned</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">
                            {issue.createdAt ? new Date(issue.createdAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No issues created yet</p>
              <button
                onClick={onCreateIssue}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Create your first issue →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
